import { APP_ROUTES } from '@lib/constants/apis';

import { isAuthRoutePath } from './is-auth-route-path';

const SENTINEL_ORIGIN = 'https://redirect-validator.invalid';
const CALLER_ORIGIN = 'https://app.example.com';

const REDIRECT_PATH_ALLOWLIST = /^[/a-zA-Z0-9._~!$&'()*+,;=:@?#%-]+$/;
const ENCODED_BACKSLASH = /%5[Cc]/;
const ENCODED_PROTOCOL_RELATIVE = /^\/%2[Ff]%2[Ff]/;

function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) {
      return true;
    }
  }
  return false;
}

function failsLeadingSlashRule(path: string): boolean {
  return !/^\/[^/]/.test(path);
}

function failsEncodedBypassChecks(path: string): boolean {
  return ENCODED_BACKSLASH.test(path) || ENCODED_PROTOCOL_RELATIVE.test(path);
}

function failsBasicPathChecks(path: string): boolean {
  return (
    path.includes('\\') ||
    hasControlChars(path) ||
    failsLeadingSlashRule(path) ||
    failsEncodedBypassChecks(path)
  );
}

function failsDecodedPathChecks(path: string): boolean {
  try {
    const decoded = decodeURIComponent(path);
    return (
      decoded.includes('\\') ||
      hasControlChars(decoded) ||
      failsLeadingSlashRule(decoded) ||
      failsEncodedBypassChecks(decoded)
    );
  } catch {
    return true;
  }
}

function failsSentinelResolution(path: string): boolean {
  let resolved: URL;
  try {
    resolved = new URL(path, SENTINEL_ORIGIN);
  } catch {
    return true;
  }

  if (resolved.origin !== SENTINEL_ORIGIN) {
    return true;
  }
  if (resolved.username || resolved.password) {
    return true;
  }

  const resolvedPath = resolved.pathname + resolved.search + resolved.hash;
  return isAuthRoutePath(resolvedPath);
}

function escapesCallerOrigin(path: string): boolean {
  try {
    return new URL(path, CALLER_ORIGIN).origin !== CALLER_ORIGIN;
  } catch {
    return true;
  }
}

function isUnsafeRedirectPath(path: string): boolean {
  if (failsBasicPathChecks(path)) {
    return true;
  }
  if (failsDecodedPathChecks(path)) {
    return true;
  }
  if (!REDIRECT_PATH_ALLOWLIST.test(path)) {
    return true;
  }
  if (failsSentinelResolution(path)) {
    return true;
  }
  return escapesCallerOrigin(path);
}

/**
 * Validates a user-supplied redirect path before trusting it as a
 * navigation destination. Returns APP_ROUTES.HOME if the path is
 * invalid or unsafe.
 *
 * Rejects:
 * - null / undefined / empty string
 * - backslash, control characters, and encoded bypass sequences
 * - paths that do not start with a single `/` (rejects bare paths,
 *   `https://…`, and `//…`)
 * - paths that resolve off-origin against a sentinel base URL
 * - auth routes (would cause a redirect loop)
 */
export function sanitizeRedirectUrl(path: string | null | undefined): string {
  if (!path) return APP_ROUTES.HOME;
  if (isUnsafeRedirectPath(path)) return APP_ROUTES.HOME;

  return path;
}
